import axios from 'axios';
import { SERVER_API_BASE } from '../config/apiBase';
import { auth } from '../utils/firebaseSetUp';
import type {
  ProductExportExtraField,
  ProductExportGroupBy,
  ProductExportOrderBy,
} from '../utils/productExportFields';
import {
  getValidSelfHostedToken,
  isSelfHostedMode,
} from './selfHostedAuth';

export type {
  ProductExportExtraField,
  ProductExportGroupBy,
  ProductExportOrderBy,
} from '../utils/productExportFields';
export { PRODUCT_EXPORT_EXTRA_FIELD_OPTIONS } from '../utils/productExportFields';

export interface ProductExportFilters {
  name?: string;
  code?: string;
  reference?: string;
  brand?: string;
  subDepartment?: string;
  hasInventory?: boolean;
  includeStock?: boolean;
  includePrice?: boolean;
  extraFields?: ProductExportExtraField[];
  orderBy?: ProductExportOrderBy;
  groupBy?: ProductExportGroupBy | '';
  limit?: number;
  offset?: number;
}

export interface ProductExportProgress {
  phase: string;
  current: number;
  total: number;
  message?: string;
}

export interface ProductExportResult {
  exportId: string;
  downloadUrl: string;
  filename: string;
  productCount: number;
  totalCount: number;
}

export interface StoredProductExport {
  id: string;
  filename: string;
  storagePath: string;
  created?: string;
  createdBy: string;
  productCount: number;
  totalCount: number;
  fileSizeBytes: number;
  filters: ProductExportFilters;
}

export interface StoredProductExportWithUrl extends StoredProductExport {
  downloadUrl: string;
}

async function getAuthToken(): Promise<string> {
  if (isSelfHostedMode()) {
    const token = await getValidSelfHostedToken();
    if (!token) {
      throw new Error('No hay sesión activa');
    }
    return token;
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('No hay sesión activa');
  }
  return user.getIdToken();
}

function buildExportPayload(
  organizationId: string,
  filters: ProductExportFilters,
  token: string
): Record<string, string | number | boolean | string[]> {
  const payload: Record<string, string | number | boolean | string[]> = {
    token,
    organizationId,
    limit: filters.limit ?? 10000,
    offset: filters.offset ?? 0,
    orderBy: filters.orderBy ?? 'subDepartment',
  };

  if (filters.name?.trim()) payload.name = filters.name.trim();
  if (filters.code?.trim()) payload.code = filters.code.trim();
  if (filters.reference?.trim()) payload.reference = filters.reference.trim();
  if (filters.brand?.trim()) payload.brand = filters.brand.trim();
  if (filters.subDepartment?.trim()) {
    payload.subDepartment = filters.subDepartment.trim();
  }
  if (filters.groupBy) {
    payload.groupBy = filters.groupBy;
  }
  if (filters.hasInventory) {
    payload.hasInventory = true;
  }
  if (filters.includeStock === false) {
    payload.includeStock = false;
  }
  if (filters.includePrice === false) {
    payload.includePrice = false;
  }
  if (filters.extraFields && filters.extraFields.length > 0) {
    payload.extraFields = filters.extraFields;
  }

  return payload;
}

export async function exportProductCatalogViaWebSocket(
  organizationId: string,
  filters: ProductExportFilters = {},
  onProgress?: (progress: ProductExportProgress) => void
): Promise<ProductExportResult> {
  const token = await getAuthToken();
  const payload = buildExportPayload(organizationId, filters, token);
  const baseURL = SERVER_API_BASE;
  const { io } = await import('socket.io-client');

  return new Promise((resolve, reject) => {
    const socket = io(baseURL, {
      transports: ['polling'],
      timeout: 60000,
      forceNew: true,
    });
    let settled = false;

    const cleanup = () => {
      socket.off('connected');
      socket.off('progress');
      socket.off('success');
      socket.off('error');
      socket.off('connect_error');
      socket.disconnect();
    };

    const finish = (handler: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      handler();
    };

    socket.on('connect_error', err => {
      finish(() =>
        reject(err instanceof Error ? err : new Error('Socket connection failed'))
      );
    });

    socket.on('connected', () => {
      socket.emit('exportProductCatalog', payload);
    });

    socket.on(
      'progress',
      (data: {
        phase?: string;
        current?: number;
        total?: number;
        message?: string;
      }) => {
        if (typeof data.current !== 'number' || typeof data.total !== 'number') {
          return;
        }
        onProgress?.({
          phase: data.phase ?? 'working',
          current: data.current,
          total: data.total,
          message: data.message,
        });
      }
    );

    socket.on('success', (data: ProductExportResult) => {
      if (data?.downloadUrl) {
        finish(() => resolve(data));
      }
    });

    socket.on('error', (payload: { message?: string }) => {
      finish(() =>
        reject(new Error(payload?.message ?? 'No se pudo exportar el catálogo'))
      );
    });
  });
}

export async function downloadProductExportPdf(
  organizationId: string,
  filters: ProductExportFilters = {}
): Promise<Blob> {
  const token = await getAuthToken();
  const params: Record<string, string | number | boolean> = {
    limit: Math.min(filters.limit ?? 100, 100),
    offset: filters.offset ?? 0,
    orderBy: filters.orderBy ?? 'subDepartment',
  };

  if (filters.name?.trim()) params.name = filters.name.trim();
  if (filters.code?.trim()) params.code = filters.code.trim();
  if (filters.reference?.trim()) params.reference = filters.reference.trim();
  if (filters.brand?.trim()) params.brand = filters.brand.trim();
  if (filters.subDepartment?.trim()) {
    params.subDepartment = filters.subDepartment.trim();
  }
  if (filters.hasInventory) {
    params.hasInventory = 'true';
  }
  if (filters.includeStock === false) {
    params.includeStock = false;
  }
  if (filters.includePrice === false) {
    params.includePrice = false;
  }
  if (filters.extraFields && filters.extraFields.length > 0) {
    params.extraFields = filters.extraFields.join(',');
  }

  const response = await axios.get(
    `${SERVER_API_BASE}/api/v1/products/${organizationId}/export`,
    {
      params,
      responseType: 'blob',
      timeout: 120000,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      paramsSerializer: {
        // Keep FastAPI-friendly repeated keys: extraFields=a&extraFields=b
        // when arrays are passed; comma-joined string is also accepted server-side.
        indexes: null,
      },
    }
  );

  const contentType = String(response.headers['content-type'] || '');
  if (contentType.includes('application/json')) {
    const text = await (response.data as Blob).text();
    let detail = 'Error al exportar productos';
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      // keep default message
    }
    throw new Error(detail);
  }

  return response.data as Blob;
}

export async function downloadStoredProductExportPdf(
  organizationId: string,
  exportId: string
): Promise<{ blob: Blob; filename: string }> {
  const headers = await authHeaders();
  const response = await axios.get(
    `${SERVER_API_BASE}/api/v1/products/${organizationId}/exports/${exportId}/download`,
    {
      responseType: 'blob',
      timeout: 300000,
      headers,
    }
  );
  const disposition = String(response.headers['content-disposition'] || '');
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] || 'catalogo-productos.pdf';
  return { blob: response.data as Blob, filename };
}

export function triggerPdfDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken();
  return { Authorization: `Bearer ${token}` };
}

export async function listProductExports(
  organizationId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<{ exports: StoredProductExport[]; hasMore: boolean }> {
  const headers = await authHeaders();
  const response = await axios.get(
    `${SERVER_API_BASE}/api/v1/products/${organizationId}/exports`,
    {
      params: {
        limit: options.limit ?? 50,
        offset: options.offset ?? 0,
      },
      headers,
    }
  );
  return response.data as { exports: StoredProductExport[]; hasMore: boolean };
}

export async function getProductExport(
  organizationId: string,
  exportId: string
): Promise<StoredProductExportWithUrl> {
  const headers = await authHeaders();
  const response = await axios.get(
    `${SERVER_API_BASE}/api/v1/products/${organizationId}/exports/${exportId}`,
    { headers }
  );
  return response.data as StoredProductExportWithUrl;
}

export async function deleteProductExport(
  organizationId: string,
  exportId: string
): Promise<void> {
  const headers = await authHeaders();
  await axios.delete(
    `${SERVER_API_BASE}/api/v1/products/${organizationId}/exports/${exportId}`,
    { headers }
  );
}

export async function redownloadStoredProductExport(
  organizationId: string,
  exportId: string
): Promise<void> {
  const { blob, filename } = await downloadStoredProductExportPdf(
    organizationId,
    exportId
  );
  triggerPdfDownload(blob, filename);
}
